import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authFetch } from "@/lib/authFetch";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { startUndoJob } from "@/lib/linkJobs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
export const KIND_LABEL = {
  link_change: "Link change", bulk: "Bulk move", problem_fix: "Problem fix", duplicate_merge: "Duplicate cleanup",
  dismiss: "Dismissed", undo: "Undo", affiliate_edit: "Affiliate edit", affiliate_create: "Affiliate added", legacy: "Earlier log",
};
export const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const StatusTag = ({ status }) => status === "not_saved" ? <Badge variant="destructive">Not saved</Badge>
  : status === "partial" ? <Badge className="bg-amber-100 text-amber-800">Partly saved</Badge> : null;

const EntryClients = ({ id }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    authFetch(`${API}/history/${id}`, { credentials: "include" }).then((r) => r.json()).then(setData).catch(() => setData({ clients: [] }));
  }, [id]);
  if (!data) return <p className="text-xs text-slate-500">Loading…</p>;
  return (
    <ul className="max-h-56 overflow-y-auto text-xs grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2" data-testid={`history-clients-${id}`}>
      {data.clients.map((c) => (
        <li key={c.client_id}>
          <Link to={`/admin/clients/${c.client_id}`} className="text-[#357c2a] hover:underline">{c.name}</Link>
          {!c.saved && <span className="text-red-600"> — not saved: {c.reason}</span>}
        </li>
      ))}
    </ul>
  );
};

const Skips = ({ title, rows }) => rows.length ? (
  <div>
    <p className="font-medium">{title} ({rows.length})</p>
    <ul className="list-disc pl-5 max-h-32 overflow-y-auto text-xs">{rows.map((r, i) => <li key={i}>{r.name} · {r.role_label}: {r.reason}</li>)}</ul>
  </div>
) : null;

const UndoButton = ({ e }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/history/${e.id}/undo-preview`, { credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Can't undo this change");
      setPreview(d);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const go = async () => {
    try {
      await startUndoJob(e.id, `Undoing: ${e.summary.slice(0, 60)}${e.summary.length > 60 ? "…" : ""}`);
      setPreview(null);
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 mt-1" onClick={open} disabled={busy} data-testid={`history-undo-${e.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />{busy ? "Checking…" : "Undo"}</Button>
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent data-testid="undo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this change?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p className="italic">{preview?.summary}</p>
                <p data-testid="undo-preview-ok"><b>{preview?.ok}</b> link{preview?.ok === 1 ? "" : "s"} will be put back as they were, in the portal and the CRM.</p>
                <Skips title="Skipped — changed since" rows={preview?.changed_since || []} />
                <Skips title="Can't put back" rows={preview?.cannot || []} />
                <p className="text-xs text-slate-500">CRM owner, Betenbough location and logins are not touched.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={go} disabled={!preview?.ok} data-testid="undo-confirm">Undo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const HistoryEntry = ({ e }) => {
  const [open, setOpen] = useState(false);
  const n = (e.client_count || 0) + (e.failed_count || 0);
  return (
    <li className="relative pl-6 pb-4 min-w-0 border-l border-slate-200 last:border-l-transparent" data-testid={`history-entry-${e.id}`}>
      <span className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${e.status === "not_saved" ? "bg-red-500" : e.status === "partial" ? "bg-amber-500" : "bg-[#357c2a]"}`} />
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>{fmtWhen(e.at)}</span><span>·</span><span>{e.admin_name || "Unknown admin"}</span>
        <Badge variant="outline" className="text-[10px]">{KIND_LABEL[e.kind] || e.kind}</Badge><StatusTag status={e.status} />
        {e.undone_by !== undefined && e.undone_at && <Badge className="bg-slate-200 text-slate-700" data-testid={`history-undone-${e.id}`}>Undone {fmtWhen(e.undone_at)}</Badge>}
        {e.undo_job_id && !e.undone_at && <Badge className="bg-sky-100 text-sky-800">Undo running</Badge>}
      </div>
      <p className="text-sm mt-1 break-words">{e.summary}</p>
      {!e.legacy && n > 0 && (
        <button className="text-xs underline text-slate-600 mt-1" onClick={() => setOpen((v) => !v)} data-testid={`history-toggle-${e.id}`}>
          {open ? "Hide clients" : `View ${n} client${n === 1 ? "" : "s"}`}
        </button>
      )}
      {open && <EntryClients id={e.id} />}
      {e.undoable && <div><UndoButton e={e} /></div>}
    </li>
  );
};

export const HistoryTimeline = ({ params, reloadKey }) => {
  const [rows, setRows] = useState(null);
  const qs = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v)).toString();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTimeout(() => setTick((t) => t + 1), 500);
    window.addEventListener("link-job-done", h);
    return () => window.removeEventListener("link-job-done", h);
  }, []);
  useEffect(() => {
    setRows(null);
    authFetch(`${API}/history?${qs}`, { credentials: "include" }).then((r) => r.json()).then((d) => setRows(d.entries || [])).catch(() => setRows([]));
  }, [qs, reloadKey, tick]);
  if (!rows) return <p className="text-sm text-slate-500 py-4">Loading history…</p>;
  if (!rows.length) return <p className="text-sm text-slate-500 py-4" data-testid="history-empty">No changes recorded yet.</p>;
  return <ul className="pt-2" data-testid="history-timeline">{rows.map((e) => <HistoryEntry key={e.id} e={e} />)}</ul>;
};

export const ClientLinkHistory = ({ clientId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/link-history`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setRows(d.entries || [])).catch(() => setRows([]));
  }, [clientId]);
  return (
    <div className="md:col-span-2 rounded-xl border bg-white p-4" data-testid="client-link-history">
      <h3 className="text-base font-semibold mb-2">Link history</h3>
      {!rows ? <p className="text-sm text-slate-500">Loading…</p> : !rows.length ? <p className="text-sm text-slate-500" data-testid="client-link-history-empty">No lender, affiliate or assigned-user changes recorded yet.</p> : (
        <ul className="space-y-2">
          {rows.map((e) => (
            <li key={e.id} className="text-sm" data-testid={`client-link-history-${e.id}`}>
              <span className="text-xs text-slate-500">{fmtWhen(e.at)} · {e.admin_name || "Unknown admin"}{e.bulk_count > 1 ? ` · part of a ${e.bulk_count}-client change` : ""}</span>
              <p className="break-words">{e.text} {!e.saved && <Badge variant="destructive" className="ml-1">Not saved{e.reason ? `: ${e.reason}` : ""}</Badge>}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
